import apiService from "./api";
import { sanitizeQueryParams } from "../utility/helpers/apiHelper";
import type {
  ModerationQueueSearchOptions,
  ReportSearchOptions,
  MassCleanupBody,
} from "@src/views/apps/content-moderation/types";

export class ContentModerationService {
  async getModerationQueue(query: ModerationQueueSearchOptions) {
    return apiService.get("/content-moderation/queue", {
      params: sanitizeQueryParams(query),
    });
  }

  async getModerationById(id: string | number) {
    return apiService.get(`/content-moderation/${id}`);
  }

  async approveModeration(id: string | number) {
    return apiService.post(`/content-moderation/${id}/approve`);
  }

  async rejectModeration(id: string | number, body: { reason: string }) {
    return apiService.post(`/content-moderation/${id}/reject`, body);
  }

  async bulkModerate(body: {
    ids: number[];
    action: "approve" | "reject";
    reason?: string;
  }) {
    return apiService.post("/content-moderation/bulk", body);
  }

  async massCleanup(body: MassCleanupBody = {}) {
    return apiService.post("/content-moderation/cleanup", body);
  }

  async getReports(query: ReportSearchOptions) {
    return apiService.get("/content-moderation/reports", {
      params: sanitizeQueryParams(query),
    });
  }

  async getReportById(id: string | number) {
    return apiService.get(`/content-moderation/reports/${id}`);
  }

  async updateReportStatus(
    id: string | number,
    body: { status: string; resolutionNotes?: string | null }
  ) {
    return apiService.patch(`/content-moderation/reports/${id}/status`, body);
  }

  async deleteReportedContent(id: string | number) {
    return apiService.delete(`/content-moderation/reports/${id}/content`);
  }

  async restrictUserFromReport(
    reportId: string | number,
    body: {
      userId: number;
      restrictionType: string;
      reason?: string | null;
      endsAt?: string | null;
    }
  ) {
    return apiService.post(
      `/content-moderation/reports/${reportId}/restrict`,
      body
    );
  }

  async banUserFromReport(
    reportId: string | number,
    body: { userId: number; reason?: string | null }
  ) {
    return apiService.post(
      `/content-moderation/reports/${reportId}/ban`,
      body
    );
  }

  async getUserRestrictions(userId: string | number) {
    return apiService.get(
      `/content-moderation/users/${userId}/restrictions`
    );
  }
}

export const contentModerationService = new ContentModerationService();
